import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { FaCopy } from 'react-icons/fa';
import api from '../../services/api';
import RapportView from './RapportView';
import RapportViewTechnique from './RapportViewTechnique';
import { fmtDateFr } from './format';
import { saveRedirect } from '../../utils/redirectAfterLogin';

// Page /rapport/:token — rapport enregistré (chiffres figés). Accès vérifié côté serveur :
// admin ou lecteur choisi à l'enregistrement.
const BROWN = '#6d4e36';

const RapportPartagePage = () => {
  const { token } = useParams();
  const [rapport, setRapport] = useState(null);
  const [error, setError]     = useState('');
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    api.get(`/rapports-partages/t/${encodeURIComponent(token)}`)
      .then(r => setRapport(r.data))
      .catch(e => {
        // Session expirée : reconnexion puis retour sur ce rapport
        if (e?.response?.status === 401) {
          saveRedirect(window.location.pathname);
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        setError(
          e?.response?.status === 403 ? "⛔ Vous n'êtes pas autorisé à lire ce rapport."
            : e?.response?.status === 404 ? 'Rapport introuvable (lien invalide ou rapport supprimé).'
            : 'Erreur lors du chargement du rapport.'
        );
      });
  }, [token]);

  if (error) return <div className="container py-4"><Alert variant="warning">{error}</Alert></div>;
  if (!rapport) return <div className="text-center py-5"><Spinner animation="border" size="sm" /> Chargement…</div>;

  const p = rapport.parametres || {};
  const showCom = p.decoupage === 'commercial' || (p.blocs || []).some(b => b.startsWith('coms.'));

  return (
    <div className="container-fluid px-3 px-md-4 pb-5" style={{ maxWidth: 1200 }}>
      <div className="my-3">
        <h4 style={{ color: BROWN, fontWeight: 700, marginBottom: 4 }}>📊 {rapport.titre}</h4>
        <div className="small text-muted">
          Créé par {rapport.createdBy || '—'} le {new Date(rapport.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
          {' · '}Données figées au {fmtDateFr(rapport.dateRef)}
        </div>
      </div>

      {rapport.type === 'point_technique'
        ? <RapportViewTechnique data={rapport.donnees} />
        : <RapportView data={rapport.donnees} showCommerciaux={showCom} canLive={rapport.isAdmin} />}

      {rapport.texte && (
        <details className="mt-4">
          <summary style={{ color: BROWN, fontWeight: 600, cursor: 'pointer' }}>Message résumé</summary>
          <div className="d-flex justify-content-end my-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={async () => {
              try { await navigator.clipboard.writeText(rapport.texte); setCopied(true); } catch { /* ignore */ }
            }}><FaCopy /> {copied ? 'Copié ✓' : 'Copier'}</button>
          </div>
          <div style={{ background: '#dcf8c6', borderRadius: 8, padding: '10px 12px', whiteSpace: 'pre-wrap', fontSize: '0.85rem', maxWidth: 520 }}>
            {rapport.texte}
          </div>
        </details>
      )}

      {rapport.isAdmin && (
        <div className="small text-muted mt-4">
          👥 Lecteurs : {rapport.lecteurs?.length ? rapport.lecteurs.map(l => l.name).join(', ') : 'admins seulement'}
          {' '}— gérables depuis la page « Point du jour ».
        </div>
      )}
    </div>
  );
};

export default RapportPartagePage;
